// Utility functions for generating image and document URLs

/**
 * Generate URL for dog profile image
 * @param dogId - The dog ID
 * @param size - Image size (S, M, L)
 * @param timestamp - Optional timestamp for cache busting
 * @returns Full URL with token and parameters
 */
export const getDogImageUrl = (dogId: number, size: 'S' | 'M' | 'L' = 'M', timestamp?: string | number): string => {
  const token = localStorage.getItem('jwt_token');
  const baseUrl = localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';
  
  if (!token) return '';
  
  const params = new URLSearchParams({
    size,
    token,
    ...(timestamp && { ts: timestamp.toString() })
  });
  
  return `${baseUrl}/api/dogs/image-url/${dogId}?${params.toString()}`;
};

/**
 * Generate URL for member profile image
 * @param memberId - The member ID
 * @param size - Image size (S, M, L)
 * @param timestamp - Optional timestamp for cache busting
 * @returns Full URL with token and parameters
 */
export const getMemberImageUrl = (memberId: number, size: 'S' | 'M' | 'L' = 'M', timestamp?: string | number): string => {
  const token = localStorage.getItem('jwt_token');
  const baseUrl = localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';
  
  if (!token) return '';
  
  const params = new URLSearchParams({
    size,
    token,
    ...(timestamp && { ts: timestamp.toString() })
  });
  
  return `${baseUrl}/api/members/user-image-url/${memberId}?${params.toString()}`;
};

/**
 * Generate URL for document download
 * @param documentId - The document ID
 * @returns Full URL with token
 */
export const getDocumentUrl = (documentId: number): string => {
  const token = localStorage.getItem('jwt_token');
  const baseUrl = localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';
  
  if (!token) return '';
  
  const params = new URLSearchParams({
    token
  });
  
  return `${baseUrl}/api/members/documents/download-url/${documentId}?${params.toString()}`;
};

/**
 * Check if an image URL should be used (has valid token)
 * @returns boolean indicating if image URLs should be generated
 */
export const shouldUseImageUrls = (): boolean => {
  return !!localStorage.getItem('jwt_token');
};

/**
 * Generate fallback storage URL with proper base URL
 * @param filename - The filename/path from the storage
 * @param timestamp - Optional timestamp for cache busting
 * @returns Full storage URL with base URL
 */
export const getStorageUrl = (filename: string, timestamp?: string | number): string => {
  const baseUrl = localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';
  
  let url;
  if (filename.startsWith('/storage/')) {
    url = `${baseUrl}${filename}`;
  } else {
    url = `${baseUrl}/storage/${filename}`;
  }
  
  if (timestamp) {
    url += `?t=${timestamp}`;
  }
  
  return url;
};